import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { api } from '../lib/api-client';
import { useAuthStore } from '../stores/auth-store';

interface ActiveSession {
  userId: string;
  name: string;
  email: string;
  role: string;
  page: string;
  lastSeen: string;
}

interface UptimeInfo {
  startedAt: string;
  uptimeMs: number;
  formatted: string;
}

/** Sends a heartbeat every 30s with the current route path */
export function useActivityHeartbeat() {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    if (!user) return;

    const send = () => {
      api.post('/activity/heartbeat', { page: location.pathname }).catch(() => {});
    };

    send(); // Send immediately on mount/route change
    const interval = setInterval(send, 30_000);
    return () => clearInterval(interval);
  }, [location.pathname, user]);
}

export function useOnlineUsers() {
  return useQuery({
    queryKey: ['activity', 'online'],
    queryFn: () => api.get<ActiveSession[]>('/activity/online'),
    refetchInterval: 15_000,
  });
}

export function useUptime() {
  return useQuery({
    queryKey: ['activity', 'uptime'],
    queryFn: () => api.get<UptimeInfo>('/activity/uptime'),
    refetchInterval: 60_000,
  });
}
